import { FC, ReactNode, useEffect, useState, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Spin } from 'antd';
import { isTokenExpired } from '@/utils/token';

interface AuthRouteProps {
  children: ReactNode;
}

const hasValidTokenInCookie = (): boolean => {
  try {
    const cookies = document.cookie.split('; ');
    const tokenCookie = cookies.find(cookie =>
      cookie.startsWith('access_token='),
    );
    if (tokenCookie) {
      const token = tokenCookie.split('=')[1];
      if (token && !isTokenExpired(token)) {
        return true;
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return false;
};

const AuthRoute: FC<AuthRouteProps> = ({ children }) => {
  const { isLoading, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [isActuallyAuthenticated, setIsActuallyAuthenticated] = useState<
    boolean | null
  >(null);
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (hasValidTokenInCookie()) {
      hasRedirectedRef.current = true;
      setIsActuallyAuthenticated(true);
      window.history.replaceState(null, '', '/');
      navigate('/', { replace: true });
      return;
    }
  }, []);

  useEffect(() => {
    if (hasRedirectedRef.current) return;
    const verify = async () => {
      setIsActuallyAuthenticated(null);
      try {
        await getAccessTokenSilently();
        setIsActuallyAuthenticated(true);
      } catch (e) {
        setIsActuallyAuthenticated(false);
      }
    };

    verify();
  }, [getAccessTokenSilently, location.key]);

  useEffect(() => {
    const checkAndRedirect = () => {
      if (hasValidTokenInCookie()) {
        window.history.replaceState(null, '', '/');
        window.location.href = '/';
        return true;
      }
      return false;
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        if (checkAndRedirect()) return;
        getAccessTokenSilently()
          .then(() => {
            window.history.replaceState(null, '', '/');
            window.location.href = '/';
          })
          .catch(() => {
            setIsActuallyAuthenticated(false);
          });
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [getAccessTokenSilently]);

  if (isLoading || isActuallyAuthenticated === null) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (isActuallyAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AuthRoute;
